import { type TranslationIDs, useIntl } from '@capsicum/common';
import { type FanTokenOfferingWithPartner } from '@capsicum/socios-core';
import { useMemo } from 'react';

import { FTOCardStatus } from '../models/fanTokenOfferingComponentsProps';

export const useFanTokenOfferingClaimButton = ({
	fto,
	hasReachedClaimEndDate
}: {
	fto: FanTokenOfferingWithPartner;
	hasReachedClaimEndDate: boolean;
}) => {
	const { $t } = useIntl();

	const hasClaimed = useMemo(() => {
		return !!fto.claimRequest && fto.claimRequest.status !== 'failed';
	}, [fto.claimRequest]);

	const actionText = useMemo(() => {
		if (hasClaimed) {
			return $t('fanTokenOffering.claim.claimed');
		}
		if (fto.status === FTOCardStatus.CLAIMABLE_PERIOD_ONGOING) {
			return $t('fanTokenOffering.claim.action');
		}

		return $t('fanTokenOffering.claim.claimSoon');
	}, [hasClaimed, fto.status]);

	const testID = useMemo(() => {
		return `ftoClaimButtonPartnerId:${fto.fanToken.partnerId}-button`;
	}, [fto.fanToken.partnerId]);

	const disabled = useMemo(() => {
		return fto.status !== FTOCardStatus.CLAIMABLE_PERIOD_ONGOING || hasClaimed;
	}, [fto.status, hasClaimed]);

	const hideButton = useMemo(() => {
		return hasReachedClaimEndDate || (fto.status === FTOCardStatus.CLAIMABLE_PERIOD_CLOSED && !fto.claimRequest);
	}, [fto.status, fto.claimRequest, hasReachedClaimEndDate]);

	const amountTranslationParams = useMemo(
		() => ({ fantokenAmount: `${fto.statistics.fanTokenTotalAmount} ${fto.fanToken.shortCode}` }),
		[fto.statistics.fanTokenTotalAmount, fto.fanToken.shortCode]
	);

	const amountTranslationKey: Partial<TranslationIDs> = useMemo(() => {
		if (hideButton) {
			return 'fanTokenOffering.claim.airdropBoughtAmount';
		}

		if (hasClaimed) {
			return 'fanTokenOffering.claim.claimedBoughtAmount';
		}

		return 'fanTokenOffering.claim.boughtAmount';
	}, [hideButton, hasClaimed]);

	return {
		actionText,
		amountTranslationKey,
		amountTranslationParams,
		disabled,
		hideButton,
		testID
	};
};
